import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  UserIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  DocumentArrowUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  CurrencyEuroIcon,
  CalendarDaysIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import UserProfileForm from '../components/UserProfileForm';
import UserProfileView from '../components/UserProfileView';
import toast from 'react-hot-toast';

const UserManagement = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [profileStats, setProfileStats] = useState(null);
  
  // Filters and pagination
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    employmentStatus: '',
    hasDocuments: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10
  });
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchUsers();
    fetchProfileStats();
  }, [filters, pagination]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const queryParams = { ...pagination };
      
      // Add active filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          queryParams[key] = value;
        }
      });

      const response = await apiService.getUsers(queryParams);
      if (response.success) {
        setUsers(response.users || []);
        setTotalPages(response.pagination?.pages || 1);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(t('users.messages.failedToFetch', { defaultValue: 'Failed to fetch users' }));
    } finally {
      setLoading(false);
    }
  };

  const fetchProfileStats = async () => {
    try {
      const response = await apiService.getUserProfileStats();
      if (response.success) {
        setProfileStats(response.stats);
      }
    } catch (error) {
      console.error('Error fetching profile stats:', error);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await apiService.deleteUser(userId);
      if (response.success) {
        toast.success(t('users.messages.deleteSuccess'));
        fetchUsers();
        fetchProfileStats();
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.message || t('users.messages.deleteFailed', { defaultValue: 'Failed to delete user' }));
    }
  };

  const handleCreateSuccess = (user) => {
    toast.success(t('users.messages.createSuccess'));
    setShowCreateForm(false);
    fetchUsers();
    fetchProfileStats();
  };

  const handleEditSuccess = (user) => {
    toast.success(t('users.messages.updateSuccess'));
    setShowEditForm(false);
    setSelectedUser(null);
    fetchUsers();
    fetchProfileStats();
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditForm(true);
    setShowViewModal(false);
  };

  const handleFilterChange = (key, value) => {
    if (value === 'all') value = '';
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="w-3 h-3 mr-1" />
          {t('users.status.active')}
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircleIcon className="w-3 h-3 mr-1" />
          {t('users.status.inactive')}
        </span>
      );
    }
  };

  const getCompletnessBadge = (percentage) => {
    let bgColor = 'bg-red-100 text-red-800';
    if (percentage >= 80) bgColor = 'bg-green-100 text-green-800';
    else if (percentage >= 50) bgColor = 'bg-yellow-100 text-yellow-800';

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${bgColor}`}>
        {percentage}%
      </span>
    );
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <ShieldCheckIcon className="w-3 h-3 mr-1" />
          {t('users.roles.administrator')}
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <WrenchScrewdriverIcon className="w-3 h-3 mr-1" />
          {t('users.roles.mechanic')}
        </span>
      );
    }
  };

  if (showCreateForm) {
    return (
      <div className="space-y-6">
        <UserProfileForm
          onSuccess={handleCreateSuccess}
          onCancel={() => setShowCreateForm(false)}
        />
      </div>
    );
  }

  if (showEditForm && selectedUser) {
    return (
      <div className="space-y-6">
        <UserProfileForm
          user={selectedUser}
          onSuccess={handleEditSuccess}
          onCancel={() => {
            setShowEditForm(false);
            setSelectedUser(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('users.title')}</h1>
          <p className="text-dark-300">{t('users.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="btn btn-primary"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create New User
        </button>
      </div>

      {/* Profile Statistics */}
      {profileStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <UserIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-dark-400">{t('users.stats.totalUsers')}</p>
                <p className="text-2xl font-bold text-white">{profileStats.totalUsers}</p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-dark-400">{t('users.stats.activeUsers')}</p>
                <p className="text-2xl font-bold text-white">{profileStats.activeUsers}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <DocumentArrowUpIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-dark-400">{t('users.stats.withDocuments')}</p>
                <p className="text-2xl font-bold text-white">{profileStats.usersWithDocuments}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <UserIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-dark-400">{t('users.stats.avgCompleteness')}</p>
                <p className="text-2xl font-bold text-white">{Math.round(profileStats.avgProfileCompleteness || 0)}%</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-dark-400" />
            <span className="text-sm font-medium text-white">{t('users.filters.title')}:</span>
          </div>

          {/* Search */}
          <div className="relative flex-1 min-w-64">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dark-400" />
            <input
              type="text"
              placeholder={t('users.searchPlaceholder')}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Role Filter */}
          <select
            value={filters.role}
            onChange={(e) => handleFilterChange('role', e.target.value)}
            className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500"
          >
            <option value="">{t('users.filters.allRoles')}</option>
            <option value="admin">{t('users.roles.admin')}</option>
            <option value="mechanic">{t('users.roles.mechanic')}</option>
          </select>

          {/* Employment Status Filter */}
          <select
            value={filters.employmentStatus}
            onChange={(e) => handleFilterChange('employmentStatus', e.target.value)}
            className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500"
          >
            <option value="">{t('users.filters.allStatus')}</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {/* Document Filter */}
          <select
            value={filters.hasDocuments}
            onChange={(e) => handleFilterChange('hasDocuments', e.target.value)}
            className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Users</option>
            <option value="true">With Documents</option>
            <option value="false">Without Documents</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            <span className="ml-2 text-dark-300">{t('users.loadingUsers')}</span>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12">
            <UserIcon className="h-12 w-12 text-dark-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">{t('users.noUsersFound')}</h3>
            <p className="text-dark-400">{t('users.createFirstUser')}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-dark-700">
                <thead className="bg-dark-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                      {t('users.table.user')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                      {t('users.table.roleStatus')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                      {t('users.table.employment')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                      {t('users.table.profile')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                      {t('users.table.documents')}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-dark-300 uppercase tracking-wider">
                      {t('users.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-dark-800 divide-y divide-dark-700">
                  {users.map((user) => (
                    <tr key={user._id} className="hover:bg-dark-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <UserIcon className="w-5 h-5 text-primary-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">{user.name}</div>
                            <div className="text-sm text-dark-400">{user.email}</div>
                            {user.phone && (
                              <div className="text-xs text-dark-500">{user.phone}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          {getRoleBadge(user.role)}
                          {getStatusBadge(user.employmentStatus || 'active')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <div className="flex items-center text-sm text-dark-300">
                            <CurrencyEuroIcon className="w-4 h-4 mr-1" />
                            €{user.hourlyRate || 0}/hr
                          </div>
                          {user.dateOfJoining && (
                            <div className="flex items-center text-xs text-dark-400">
                              <CalendarDaysIcon className="w-3 h-3 mr-1" />
                              {formatDate(user.dateOfJoining)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getCompletnessBadge(user.profileCompleteness || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-dark-300">
                          <DocumentArrowUpIcon className="w-4 h-4 mr-1" />
                          {user.documents?.length || 0} {t('users.files')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewUser(user)}
                            className="text-blue-400 hover:text-blue-300"
                            title={t('users.actions.viewProfile')}
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditUser(user)}
                            className="text-yellow-400 hover:text-yellow-300"
                            title={t('users.actions.editProfile')}
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user._id, user.name)}
                            className="text-red-400 hover:text-red-300"
                            title={t('users.actions.deleteUser')}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-3 border-t border-dark-700 flex items-center justify-between">
                <div className="text-sm text-dark-400">
                  {t('users.pagination.page', { current: pagination.page, total: totalPages })}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="px-3 py-1 bg-dark-700 text-white rounded disabled:opacity-50"
                  >
                    {t('users.pagination.previous')}
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === totalPages}
                    className="px-3 py-1 bg-dark-700 text-white rounded disabled:opacity-50"
                  >
                    {t('users.pagination.next')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* View Modal */}
      {showViewModal && selectedUser && (
        <UserProfileView
          userId={selectedUser._id}
          onEdit={handleEditUser}
          onClose={() => {
            setShowViewModal(false);
            setSelectedUser(null);
          }}
        />
      )}
    </div>
  );
};

export default UserManagement;